import { List, Pencil, Plus, Trash2, X } from 'lucide-react'
import { FormProvider } from 'react-hook-form'

import { useAttributesTable } from '@/hooks/features/attributes/useAttributesTable'
import { useDataTable } from '@/hooks/ui'

import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { ColumnVisibilityDropdown, DataTable, NoData } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination-controls'
import { Switch } from '@/components/ui/switch'

import { ConfirmDialog, PageHeader, StatsCards, StatusBadge } from '@/components/atoms'
import { ActionDropdown } from '@/components/atoms'
import { AttributeValueDialog } from '@/components/features/attributes/AttributeValueDialog.tsx'

import { AttributeModel } from '@/types'

import { AttributeDialog } from './AttributeDialog'
import { AttributesFilters } from './AttributesFilters'

type TableColumns = 'name' | 'products_count' | 'status' | 'value_count' | 'actions'
const DEFAULT_VISIBLE_COLUMNS: TableColumns[] = [
  'name',
  'products_count',
  'status',
  'value_count',
  'actions',
]

export const AttributesTable = () => {
  const {
    loading,
    data,
    pagination,
    formMethods,
    stats,
    mode,
    valueMode,
    attributeDialogOpen,
    attributeValueDialogOpen,
    createAttributeValue,
    editAttribute,
    editAttributeValue,
    deleteDialogOpen,
    deleteValueDialogOpen,
    attributeToDelete,
    attributeValueToDelete,
    createLoading,
    updateLoading,
    deleteLoading,
    setPage,
    setPageSize,
    openCreateDialog,
    openCreateValueDialog,
    openViewDialog,
    openViewValueDialog,
    openEditDialog,
    openEditValueDialog,
    closeDialog,
    closeValueDialog,
    handleDeleteClick,
    handleDeleteValueClick,
    handleDeleteConfirm,
    handleDeleteValueConfirm,
    setDeleteDialogOpen,
    setDeleteValueDialogOpen,
    handleCreateAttribute,
    handleCreateAttributeValue,
    handleUpdateAttribute,
    handleUpdateAttributeValue,
    toggleStatus,
    selectedAttribute,
  } = useAttributesTable()

  const { columns, changeColumnVisibility, disabledColumns } = useDataTable<
    AttributeModel.Attribute,
    TableColumns
  >({
    columns: [
      {
        accessorKey: 'name',
        columnLabel: () => 'Name',
        cell: (data) => <span className="font-medium text-ink">{data?.name}</span>,
        hasSorting: true,
      },

      {
        accessorKey: 'products_count',
        columnLabel: () => 'Products',
        cell: (data) => <span className="tabular-nums text-ink">{data?.products_count}</span>,
      },
      {
        accessorKey: 'status',
        columnLabel: () => 'Status',
        cell: (data) => (
          <div className="flex items-center gap-2">
            <StatusBadge
              type="product"
              status={data?.status === 'active' ? 'active' : 'inactive'}
            />
            <Switch
              checked={data?.status === 'active'}
              onCheckedChange={() => toggleStatus(data)}
            />
          </div>
        ),
      },
      {
        accessorKey: 'value_count',
        columnLabel: () => 'Values',
        cell: (data) => (
          <div className="flex flex-wrap items-center gap-1.5">
            {data?.values?.map((value) => (
              <span
                key={value._id}
                className="inline-flex h-6 items-center gap-0.5 rounded-sm bg-muted pl-2 pr-0.5 text-xs font-medium text-ink"
              >
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 hover:underline"
                  onClick={(e) => {
                    e.stopPropagation()
                    openEditValueDialog(value)
                  }}
                >
                  {data.is_color && value.color_code && (
                    <span
                      className="size-3 rounded-full border border-border"
                      style={{ backgroundColor: value.color_code }}
                    />
                  )}
                  {value.name}
                </button>
                <button
                  type="button"
                  aria-label={`Delete ${value.name}`}
                  className="inline-flex size-5 items-center justify-center rounded-sm text-muted-foreground hover:bg-fill-hover hover:text-ink"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteValueClick(value)
                  }}
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
            <button
              type="button"
              aria-label="Add value"
              onClick={() => openCreateValueDialog(data)}
              className="inline-flex size-6 items-center justify-center rounded-sm bg-muted text-ink hover:bg-fill-hover"
            >
              <Plus className="size-3.5" />
            </button>
          </div>
        ),
      },
      {
        accessorKey: 'actions',
        columnLabel: () => 'Actions',
        cell: (data) => (
          <div className="flex justify-end">
            <ActionDropdown
              items={[
                {
                  label: 'View',
                  icon: List,
                  onClick: () => openViewDialog(data),
                },
                {
                  label: 'Edit',
                  icon: Pencil,
                  onClick: () => openEditDialog(data),
                },
                {
                  label: 'Delete',
                  icon: Trash2,
                  variant: 'destructive',
                  onClick: () => handleDeleteClick(data),
                },
              ]}
            />
          </div>
        ),
        classes: () => ({
          column: 'text-right',
          cell: 'text-right',
        }),
        isAction: true,
      },
    ],
    visibleColumns: DEFAULT_VISIBLE_COLUMNS,
    disabledColumns: ['actions'],
  })

  return (
    <div className="space-y-4">
      <PageHeader
        title="Attributes"
        description="Options used for product variants, such as size, color or material."
        onAddClick={openCreateDialog}
        addButtonText="Add attribute"
      />

      <StatsCards
        stats={[
          {
            title: 'Total attributes',
            value: stats.totalAttributes,
            icon: List,
          },
          {
            title: 'Active attributes',
            value: stats.activeAttributes,
          },
          {
            title: 'Inactive attributes',
            value: stats.inactiveAttributes,
          },
        ]}
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Pagination.PageSizeDropdown
                pageSize={pagination.pageSize}
                onPageSizeChange={setPageSize}
              />
              <FormProvider {...formMethods}>
                <AttributesFilters />
              </FormProvider>
            </div>
            <ColumnVisibilityDropdown
              columns={columns}
              onColumnVisibilityChange={changeColumnVisibility}
              disabledColumns={disabledColumns}
            />
          </div>
        </CardHeader>
        <CardContent>
          {!loading && data.length === 0 ? (
            <NoData
              title="No attributes found"
              btnText="Add attribute"
              openCreateDialog={openCreateDialog}
            />
          ) : (
            <>
              <DataTable columns={columns as any} data={data} loading={loading} />

              <Pagination.Buttons
                currentPage={pagination.currentPage}
                totalItems={pagination.totalCount}
                pageSize={pagination.pageSize}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      <AttributeDialog
        open={attributeDialogOpen}
        mode={mode}
        editAttribute={editAttribute}
        closeDialog={closeDialog}
        onCreate={handleCreateAttribute}
        onUpdate={handleUpdateAttribute}
        loading={createLoading || updateLoading}
      />

      <AttributeValueDialog
        open={attributeValueDialogOpen}
        mode={valueMode}
        createAttributeValue={createAttributeValue}
        editAttributeValue={editAttributeValue}
        closeDialog={closeValueDialog}
        onCreate={handleCreateAttributeValue}
        onUpdate={handleUpdateAttributeValue}
        loading={createLoading || updateLoading}
        isColorAttribute={!!selectedAttribute?.is_color}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete attribute?"
        description={`This will permanently delete the attribute "${attributeToDelete?.name}". This action cannot be undone.`}
        confirmText={deleteLoading ? 'Deleting...' : 'Delete'}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        variant="destructive"
      />

      <ConfirmDialog
        open={deleteValueDialogOpen}
        onOpenChange={setDeleteValueDialogOpen}
        title="Delete value?"
        description={`This will permanently delete the attribute value "${attributeValueToDelete?.name}". This action cannot be undone.`}
        confirmText={deleteLoading ? 'Deleting...' : 'Delete'}
        onConfirm={handleDeleteValueConfirm}
        loading={deleteLoading}
        variant="destructive"
      />
    </div>
  )
}
